import { ComponentDataSourceService, DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import {
  GROUP_CALENDAR_VIEW_ROLES_KEY,
  GROUP_CALENDAR_EDIT_PATH_IDS_KEY,
  GROUP_CALENDAR_DEFAULT_VIEW_ROLES,
  GROUP_CALENDAR_ALWAYS_ON_ROLES
} from '../../../../components/duna/duna-org-settings/duna-org-settings.component';
import {
  extractPathIdsFromEducationalPaths,
  resolveUserPathIdsFromUserInfo,
  userCanViewGroupCalendar
} from '../../../../components/duna/duna-events-group-calendar/group-calendar-access.utils';
import { UserService } from '../../../../services/user/user.service';

function isOrgSettingTrue(value: unknown): boolean {
  return value === true || value === 'true' || value === 1;
}

function resolveOrgIdFromUser(user: Record<string, unknown> | null | undefined): number | null {
  if (!user) return null;
  const orgId =
    user['organizations_id'] ??
    (user['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
  if (orgId == null) return null;
  const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId), 10);
  return Number.isFinite(n) ? n : null;
}

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter((item) => item.length > 0);
  }
  return [];
}

function normalizeNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const n = typeof item === 'number' ? item : parseInt(String(item), 10);
      return Number.isFinite(n) ? n : null;
    })
    .filter((n): n is number => n !== null);
}

function checkVisibility(
  service: ComponentDataSourceService,
  orgId: number
): Observable<{ visible: boolean }> {
  const obs = service.executeDataSource('org_settings', { organizationId: orgId });
  if (!obs) return of({ visible: false });

  return obs.pipe(
    switchMap((res: { data?: Record<string, unknown> }) => {
      const data = res?.data ?? {};
      if (!isOrgSettingTrue(data['group_calendar_enabled'])) {
        return of({ visible: false });
      }

      const rawRoles = normalizeStringArray(data[GROUP_CALENDAR_VIEW_ROLES_KEY]);
      const viewRoles = rawRoles.length > 0 ? rawRoles : [...GROUP_CALENDAR_DEFAULT_VIEW_ROLES, ...GROUP_CALENDAR_ALWAYS_ON_ROLES];
      const editPathIds = normalizeNumberArray(data[GROUP_CALENDAR_EDIT_PATH_IDS_KEY]);

      const authService = service.authService;
      const currentUser = authService.getCurrentUser() as Record<string, unknown> | null;
      const userRoles: string[] = authService.getUserRoles();
      const userInfo = (currentUser?.['user_info'] as Record<string, unknown>) ?? {};

      const pathIdsFromToken = resolveUserPathIdsFromUserInfo(userInfo);
      if (pathIdsFromToken !== null) {
        return of({
          visible: userCanViewGroupCalendar({
            userRoles,
            userInfo,
            viewRoles,
            editPathIds,
            userPathIds: pathIdsFromToken
          })
        });
      }

      const userService = service.injector.get(UserService);
      return userService.getEducationalPaths().pipe(
        map((paths) => ({
          visible: userCanViewGroupCalendar({
            userRoles,
            userInfo,
            viewRoles,
            editPathIds,
            userPathIds: extractPathIdsFromEducationalPaths(paths ?? [])
          })
        }))
      );
    }),
    catchError(() => of({ visible: false }))
  );
}

/**
 * Returns { visible: boolean } — whether the logged-in user may see the user-group calendar.
 * Checks group_calendar_enabled org flag, view roles, and configured uddannelsesveje.
 */
export function duna_user_group_calendar_visible(service: ComponentDataSourceService): DataSourceFunction {
  return {
    name: 'duna_user_group_calendar_visible',
    description:
      'Whether the user-group calendar is visible to the current user (group_calendar_enabled + role/path check). Returns { visible: boolean }.',
    execute: (_params?: LoadDataParams): Observable<{ visible: boolean }> => {
      const authService = service.authService;
      if (!authService.isAuthenticated) {
        return of({ visible: false });
      }

      const orgIdFromToken = resolveOrgIdFromUser(authService.getCurrentUser() as Record<string, unknown> | null);
      if (orgIdFromToken != null) {
        return checkVisibility(service, orgIdFromToken);
      }

      return authService.getUserInfo().pipe(
        take(1),
        switchMap((info) => {
          const orgId = resolveOrgIdFromUser(info as Record<string, unknown>);
          if (orgId == null) return of({ visible: false });
          return checkVisibility(service, orgId);
        }),
        catchError(() => of({ visible: false }))
      );
    }
  };
}
